import type { AccrualMonth, AdjustedRow, FinanceBasis, FinanceBsSummary, FinanceCashPoint, FinancePlRow, FinanceResponse } from "../../../types/pl";
import { StateBanner } from "../../../components/StateBanner";
import { BasisBadge } from "../../../components/BasisBadge";
import { man, profitColor, yen } from "../../../lib/plFormat";

/** 'YYYY-MM' → 'YY/MM'（表の列見出し） */
function shortMonth(m: string): string {
  return `${m.slice(2, 4)}/${m.slice(5, 7)}`;
}

/** 表セルの金額（円・桁区切りのみ。負数は「−」付き）。null は呼び出し側で「—」にする */
function cellNum(n: number): string {
  const body = Math.round(Math.abs(n)).toLocaleString("ja-JP");
  return n < 0 ? `−${body}` : body;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function rowClass(r: FinancePlRow): string {
  if (r.key === "acc:unpaid") return "pl-finance-row--info";
  if (r.section === "adjusted") return "pl-finance-row--adjusted";
  if (r.is_profit) return "pl-finance-row--profit";
  if (r.is_total) return "pl-finance-row--total";
  return "";
}

/** 棚卸調整の内訳ツールチップ（前月末在庫・当月仕入・当月末在庫。在庫 = 倉庫在庫 + 在途品） */
function adjustedTooltip(adjusted: AdjustedRow[], month: string): string | undefined {
  const idx = adjusted.findIndex((a) => a.month === month);
  if (idx < 0) return undefined;
  const adj = adjusted[idx];
  const prev = idx > 0 ? adjusted[idx - 1] : undefined;
  const y = (n: number | null) => (n == null ? "—" : yen(n));
  const begin =
    adj.inv_begin == null
      ? "—"
      : prev && prev.wh_end != null
        ? `${yen(adj.inv_begin)}（倉庫 ${y(prev.wh_end)} + 在途 ${yen(prev.transit_end)}）`
        : `${yen(adj.inv_begin)}${prev ? "" : "（期首棚卸高）"}`;
  const end =
    adj.inv_end == null
      ? adj.wh_end == null
        ? "—（倉庫在庫未入力）"
        : "—"
      : `${yen(adj.inv_end)}（倉庫 ${y(adj.wh_end)} + 在途 ${yen(adj.transit_end)}）`;
  return `前月末在庫 ${begin} / 当月仕入 ${y(adj.purchases)} / 当月末在庫 ${end}`;
}

function KpiCard({ label, value, color, sub, title }: { label: string; value: string; color?: string; sub?: string; title?: string }) {
  return (
    <div className="pl-kpi-card">
      <span className="pl-kpi-label">{label}</span>
      <span className="pl-kpi-value" style={color ? { color } : undefined}>
        {value}
      </span>
      {sub && (
        <span className="pl-kpi-sub" title={title}>
          {sub}
        </span>
      )}
    </div>
  );
}

function PlTable({ months, rows, adjusted }: { months: string[]; rows: FinancePlRow[]; adjusted: AdjustedRow[] }) {
  return (
    <div className="pl-table-wrap">
      <table className="pl-finance-table">
        <thead>
          <tr>
            <th>科目</th>
            {months.map((m) => (
              <th key={m} className="pl-finance-num">
                {shortMonth(m)}
              </th>
            ))}
            <th className="pl-finance-num pl-finance-num--cum">累計</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={rowClass(r)}>
              <td style={{ paddingLeft: 8 + (Math.max(1, r.level) - 1) * 12 }}>{r.label}</td>
              {r.amounts.map((a, i) => {
                const adjCell = r.section === "adjusted" ? adjusted.find((x) => x.month === months[i]) : undefined;
                const title = adjCell ? adjustedTooltip(adjusted, adjCell.month) : undefined;
                return (
                  <td
                    key={months[i] ?? i}
                    className={`pl-finance-num ${a == null ? "pl-finance-empty" : ""}`}
                    style={a != null && a < 0 ? { color: "var(--pl-neg)" } : undefined}
                    title={title}
                  >
                    {a != null ? cellNum(a) : adjCell?.status === "missing_inventory" ? <span style={{ fontSize: 11 }}>—（倉庫在庫未入力）</span> : "—"}
                  </td>
                );
              })}
              <td
                className={`pl-finance-num pl-finance-num--cum ${r.cumulative == null ? "pl-finance-empty" : ""}`}
                style={r.cumulative != null && r.cumulative < 0 ? { color: "var(--pl-neg)" } : undefined}
              >
                {r.cumulative == null ? "—" : cellNum(r.cumulative)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BsRow({ label, value, kind }: { label: string; value: number; kind?: "sub" | "head" | "total" }) {
  const cls = kind === "sub" ? "pl-finance-bs-row--sub" : kind === "head" ? "pl-finance-bs-row--head" : kind === "total" ? "pl-finance-bs-row--total" : "";
  return (
    <div className={`pl-finance-bs-row ${cls}`}>
      <span>{label}</span>
      <span className="pl-finance-bs-val" style={{ color: value < 0 ? "var(--pl-neg)" : undefined }}>
        {yen(value)}
      </span>
    </div>
  );
}

function BsCard({ bs }: { bs: FinanceBsSummary | null }) {
  if (!bs) {
    return (
      <div className="pl-card">
        <span className="pl-card-title">B/S サマリー</span>
        <div className="pl-empty">この月の B/S は未取込です。</div>
      </div>
    );
  }
  const cardTotal = bs.card_payables.reduce((s, l) => s + l.balance, 0);
  return (
    <div className="pl-card">
      <div className="pl-card-head">
        <span className="pl-card-title">B/S サマリー（{bs.month} 末）</span>
        <span className="pl-card-sub">freee 計上額</span>
      </div>
      <div className="pl-finance-bs-list">
        <BsRow label="現金・預金" value={bs.cash.total} kind="head" />
        {bs.cash.accounts.map((a) => (
          <BsRow key={a.key} label={a.label} value={a.balance} kind="sub" />
        ))}
        {bs.card_payables.length > 0 && (
          <>
            <BsRow label="カード未払" value={cardTotal} kind="head" />
            {bs.card_payables.map((c) => (
              <BsRow key={c.key} label={c.label} value={c.balance} kind="sub" />
            ))}
          </>
        )}
        <BsRow label="未払金" value={bs.accounts_payable} />
        <BsRow label="預り金" value={bs.deposits_received} />
        <BsRow label="預け金" value={bs.deposits_paid} />
        <BsRow label="役員借入金" value={bs.officer_loan} />
        {bs.short_term_loan !== 0 && <BsRow label="短期借入金" value={bs.short_term_loan} />}
        <BsRow label="純資産" value={bs.net_assets} kind="total" />
        <BsRow label="資産合計" value={bs.total_assets} kind="total" />
      </div>
    </div>
  );
}

function CashTrendCard({ trend }: { trend: FinanceCashPoint[] }) {
  const max = Math.max(0, ...trend.map((p) => p.total ?? 0));
  return (
    <div className="pl-card">
      <div className="pl-card-head">
        <span className="pl-card-title">現預金推移</span>
        <span className="pl-card-sub">各月末の現金・預金合計</span>
      </div>
      <div className="pl-month-bars" role="img" aria-label="現預金の月末残高推移">
        {trend.map((p) => (
          <div className="pl-month-bar-col" key={p.month}>
            {p.total == null ? (
              <div className="pl-month-bar pl-month-bar--empty" title="未取込" />
            ) : (
              <>
                <span className="pl-month-bar-val">{man(p.total)}円</span>
                <div className="pl-month-bar" style={{ height: `${max > 0 ? Math.max(1, (Math.max(0, p.total) / max) * 78) : 1}%` }} title={yen(p.total)} />
              </>
            )}
          </div>
        ))}
      </div>
      <div className="pl-month-bar-labels">
        {trend.map((p) => (
          <span key={p.month}>{Number(p.month.slice(5, 7))}月</span>
        ))}
      </div>
    </div>
  );
}

const BASIS_OPTIONS: { key: FinanceBasis; label: string }[] = [
  { key: "deposit", label: "入金ベース（freee）" },
  { key: "accrual", label: "発生ベース（出荷月）" },
];

function BasisControl({ value, accrualAvailable, onChange }: { value: FinanceBasis; accrualAvailable: boolean; onChange: (b: FinanceBasis) => void }) {
  return (
    <div className="pl-mode-switch pl-finance-basis" role="group" aria-label="計上基準">
      {BASIS_OPTIONS.map((o) => {
        const disabled = o.key === "accrual" && !accrualAvailable;
        return (
          <button
            key={o.key}
            type="button"
            className={`pl-mode-btn ${value === o.key ? "active" : ""}`}
            aria-pressed={value === o.key}
            disabled={disabled}
            title={disabled ? "同期後に利用できます" : undefined}
            onClick={() => onChange(o.key)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function AccrualBreakdown({ months }: { months: AccrualMonth[] }) {
  const th = (t: string) => <th className="pl-finance-num">{t}</th>;
  return (
    <details className="pl-finance-breakdown">
      <summary>引き直しの内訳</summary>
      <div className="pl-table-wrap">
        <table className="pl-finance-table">
          <thead>
            <tr>
              <th>月</th>
              {th("入金売上")}
              {th("発生売上")}
              {th("差")}
              {th("入金手数料")}
              {th("発生手数料")}
              {th("Makuake広告費（入金→発生）")}
              {th("未入金累計")}
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const diff = m.sales_accrual - m.sales_deposit;
              return (
                <tr key={m.month}>
                  <td>{shortMonth(m.month)}</td>
                  <td className="pl-finance-num">{cellNum(m.sales_deposit)}</td>
                  <td className="pl-finance-num">{cellNum(m.sales_accrual)}</td>
                  <td className="pl-finance-num" style={diff < 0 ? { color: "var(--pl-neg)" } : undefined}>
                    {cellNum(diff)}
                  </td>
                  <td className="pl-finance-num">{cellNum(m.fee_deposit)}</td>
                  <td className="pl-finance-num">
                    {cellNum(m.fee_accrual)} <BasisBadge basis={m.fee_basis} />
                  </td>
                  <td className="pl-finance-num">
                    {cellNum(m.ad_deposit)} → {cellNum(m.ad_accrual)}
                  </td>
                  <td className="pl-finance-num pl-finance-num--cum">{cellNum(m.unpaid_cumulative)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function adjustedSub(adj: AdjustedRow | undefined, value: number | null): string {
  if (value != null) return `販売原価ベース ${yen(value)}`;
  if (adj?.status === "missing_inventory") return "販売原価ベース —（倉庫在庫未入力）";
  return "販売原価ベース —（在庫未入力）";
}

export function FinanceTab({
  data,
  basis,
  onBasisChange,
}: {
  data: FinanceResponse;
  /** 選択中の計上基準。null = 未指定（accrual があれば発生ベース） */
  basis: FinanceBasis | null;
  onBasisChange: (b: FinanceBasis) => void;
}) {
  const { meta, bs, cash_trend } = data;

  if (!meta.connected) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <StateBanner kind="missing" message="freee 未連携です。取込タブの「freee 連携」から認可コードを登録してください。" />
        {meta.last_error && <p className="pl-footer-note">直近のエラー: {meta.last_error}</p>}
      </div>
    );
  }

  const monthMissing = !meta.months_available.includes(meta.selected_month);
  const adjSel = data.adjusted.find((a) => a.month === meta.selected_month);

  const accrual = data.accrual;
  const effectiveBasis: FinanceBasis = accrual ? (basis ?? "accrual") : "deposit";
  const useAccrual = effectiveBasis === "accrual" && accrual != null;
  const kpis = useAccrual ? accrual.kpis : data.kpis;
  const plRows = useAccrual ? accrual.pl_rows : data.pl.rows;
  const accSel = accrual?.months.find((m) => m.month === meta.selected_month);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {monthMissing && (
        <StateBanner
          kind="missing"
          message={`${meta.selected_month} の試算表は未取込です（取込タブ「freee 試算表を今すぐ同期」）`}
        />
      )}

      <div className="pl-finance-basis-wrap">
        <BasisControl value={effectiveBasis} accrualAvailable={accrual != null} onChange={onBasisChange} />
        {useAccrual && (
          <div className="pl-footer-note pl-finance-basis-note">
            <span>売上高・モール手数料・Makuake 手数料/広告費を出荷月に引き直し。未入金分は『未入金売上』行（売掛相当）</span>
            {accSel && <BasisBadge basis={accSel.fee_basis} note="モール手数料の根拠" />}
            {accSel?.notes.map((n, i) => (
              <span key={`an-${i}`}>・{n}</span>
            ))}
          </div>
        )}
      </div>

      <div className="pl-finance-status">
        {monthMissing ? null : meta.up_to_date ? (
          <span className="pl-badge pl-badge--ok">最新</span>
        ) : (
          <span className="pl-badge pl-badge--unconfirmed">集計中（freee）</span>
        )}
        <span>freee 取得 {fmtDateTime(meta.fetched_at)}</span>
        <span>・ 最終同期 {fmtDateTime(meta.last_sync_at)}</span>
        {meta.last_error && <span style={{ color: "var(--pl-neg)" }}>・ エラー: {meta.last_error}</span>}
      </div>

      <div className="pl-kpi-grid pl-finance-kpi">
        <KpiCard label="売上高" value={kpis.sales == null ? "—" : yen(kpis.sales)} color={kpis.sales == null ? undefined : profitColor(kpis.sales)} sub={`${meta.selected_month} 当月`} />
        <KpiCard
          label="売上総利益"
          value={kpis.gross_profit == null ? "—" : yen(kpis.gross_profit)}
          color={kpis.gross_profit == null ? undefined : profitColor(kpis.gross_profit)}
        />
        <KpiCard
          label="営業利益"
          value={kpis.operating_profit == null ? "—" : yen(kpis.operating_profit)}
          color={kpis.operating_profit == null ? undefined : profitColor(kpis.operating_profit)}
          sub={adjustedSub(adjSel, kpis.operating_profit_adjusted)}
          title={adjustedTooltip(data.adjusted, meta.selected_month)}
        />
        <KpiCard
          label="当期純利益"
          value={kpis.net_profit == null ? "—" : yen(kpis.net_profit)}
          color={kpis.net_profit == null ? undefined : profitColor(kpis.net_profit)}
        />
        <KpiCard label="現預金合計" value={kpis.cash_total == null ? "—" : yen(kpis.cash_total)} color={kpis.cash_total == null ? undefined : profitColor(kpis.cash_total)} sub="月末残高" />
      </div>

      <div className="pl-card">
        <div className="pl-card-head">
          <span className="pl-card-title">{useAccrual ? "月次推移 P/L（発生ベース・出荷月）" : "月次推移 P/L（freee）"}</span>
          <span className="pl-card-sub">
            単位: 円 ・ 会計期間 {meta.fiscal_start} 〜 ・ 累計は期首〜{meta.selected_month}
          </span>
        </div>
        <PlTable months={data.pl.months} rows={plRows} adjusted={data.adjusted} />
      </div>

      {useAccrual && accrual && <AccrualBreakdown months={accrual.months} />}

      <div className="pl-two-col">
        <BsCard bs={bs} />
        <CashTrendCard trend={cash_trend} />
      </div>

      <p className="pl-footer-note" style={{ margin: 0 }}>
        月末在庫 = 倉庫在庫 + 在途品（支払済み・倉庫の在庫報告に未反映の輸入仕入・諸掛・運賃）
      </p>

      <p className="pl-footer-note" style={{ margin: 0 }}>
        税理士方針では freee は期中 入金日基準・期末に売掛金で見越し。発生ベースはダッシュボード上の引き直し
      </p>

      {meta.notes.length > 0 && (
        <div>
          {meta.notes.map((n, i) => (
            <p className="pl-footer-note" key={`fn-${i}`} style={{ margin: "0 0 4px" }}>
              ・{n}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
